import { Layers, X } from "lucide-react";
import type { BatchResponse, EvaluationResponse } from "../../gen/models";

interface BatchAssignModalProps {
  isOpen: boolean;
  evaluations: EvaluationResponse[];
  activeBatches: BatchResponse[];
  targetBatchId: string;
  newBatchTitle: string;
  creatingBatchInline: boolean;
  assigningEvaluations: boolean;
  assignAllEvaluations?: boolean;
  onSetAssignAllEvaluations?: (val: boolean) => void;
  onClose: () => void;
  onSetTargetBatchId: (id: string) => void;
  onSetNewBatchTitle: (title: string) => void;
  onSetCreatingBatchInline: (val: boolean) => void;
  onAssignEvaluations: () => void;
}

export function BatchAssignModal({
  isOpen,
  evaluations,
  activeBatches,
  targetBatchId,
  newBatchTitle,
  creatingBatchInline,
  assigningEvaluations,
  assignAllEvaluations = true,
  onSetAssignAllEvaluations,
  onClose,
  onSetTargetBatchId,
  onSetNewBatchTitle,
  onSetCreatingBatchInline,
  onAssignEvaluations,
}: BatchAssignModalProps) {
  if (!isOpen) return null;

  const isSingle = evaluations.length === 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
      <div className="bg-stone-900 border border-stone-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden p-5 space-y-4">
        <div className="flex items-center justify-between border-b border-stone-800 pb-3">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-bold text-stone-100">
              {creatingBatchInline
                ? "Create Feedback Batch"
                : isSingle
                ? "Add Evaluation to Batch"
                : "Add Evaluations to Batch"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 hover:text-stone-200 p-1 cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3 text-xs">
          {!creatingBatchInline ? (
            <div>
              <p className="text-stone-400 mb-3">
                {isSingle
                  ? "Assign this evaluation from this thread to an active curation batch:"
                  : `Assign ${evaluations.length} evaluations from this thread to an active curation batch:`}
              </p>
              <label className="block font-semibold text-stone-300 mb-1">
                Select Active Batch
              </label>
              {activeBatches.length > 0 ? (
                <select
                  value={targetBatchId}
                  onChange={(e) => onSetTargetBatchId(e.target.value)}
                  className="w-full rounded-xl bg-stone-800 border border-stone-700 p-2 text-xs text-stone-100 focus:outline-hidden focus:ring-1 focus:ring-emerald-500"
                >
                  {activeBatches.map((b) => (
                    <option key={b.id} value={b.id} className="bg-stone-800 text-stone-100">
                      {b.title} ({b.comments_count ?? 0} comments)
                    </option>
                  ))}
                </select>
              ) : (
                <div className="p-2.5 rounded-xl bg-stone-800/60 border border-stone-700 text-stone-400 text-xs">
                  No active curation batches found. Create one below to get started.
                </div>
              )}
              <button
                type="button"
                onClick={() => onSetCreatingBatchInline(true)}
                className="text-emerald-400 font-semibold text-[11px] mt-2.5 block hover:underline cursor-pointer"
              >
                + Create New Batch Instead
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="block font-semibold text-stone-300">
                New Batch Title
              </label>
              <input
                type="text"
                placeholder="e.g., Don Silvestre Minimum Stay Rule Curation"
                value={newBatchTitle}
                onChange={(e) => onSetNewBatchTitle(e.target.value)}
                className="w-full rounded-xl bg-stone-800 border border-stone-700 p-2 text-xs text-stone-100 placeholder:text-stone-500 focus:outline-hidden focus:ring-1 focus:ring-emerald-500"
              />

              {/* Assign evaluations checkbox */}
              <div className="p-3 rounded-xl border border-stone-800 bg-stone-950/60 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-stone-200">
                    {isSingle
                      ? "Assign evaluation to batch"
                      : "Assign all evaluations to batch"}
                  </div>
                  <p className="text-[11px] text-stone-400 mt-0.5">
                    {isSingle
                      ? "Attaches this evaluation from this thread upon creation."
                      : `Attaches all ${evaluations.length} evaluations from this thread upon creation.`}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={assignAllEvaluations}
                  onChange={(e) => onSetAssignAllEvaluations?.(e.target.checked)}
                  className="w-4 h-4 accent-emerald-500 rounded cursor-pointer shrink-0 ml-3"
                />
              </div>

              <button
                type="button"
                onClick={() => onSetCreatingBatchInline(false)}
                className="text-stone-400 hover:text-stone-200 text-[11px] block mt-1 hover:underline cursor-pointer"
              >
                ← Select Existing Batch
              </button>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-stone-800">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-xl text-xs font-medium text-stone-300 hover:bg-stone-800 transition cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onAssignEvaluations}
            disabled={assigningEvaluations || (!creatingBatchInline && !targetBatchId && activeBatches.length === 0)}
            className="px-4 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs disabled:opacity-50 transition shadow-xs cursor-pointer"
          >
            {assigningEvaluations
              ? "Assigning..."
              : isSingle
              ? "Assign Evaluation"
              : "Assign Evaluations"}
          </button>
        </div>
      </div>
    </div>
  );
}
